/**
 * Sheet viewer: pinch zoom 1–4×, page-turn keys/pedals, instant connector jumps with pulse +
 * "↩ Til baka", and the overlay slot for annotations (M4), in the page layout chosen on the device
 * (viewer/pageMode — "Útlit síðu"):
 *  - Samfellt: continuous vertical scroll of all pages, fitted to the width. A connector jump puts
 *    its point ~25 % down the viewport but always keeps the target page the current one (layout.ts
 *    scrollForPoint);
 *  - Heil síða: one whole page at a time (spreads of one page, spreads.ts);
 *  - Tvær síður (landscape, ≥ 1000 px wide): spreads of two pages (odd last page alone).
 * A double-tap in the middle switches Samfellt ↔ Heil síða (zoomed in: back to zoom 1).
 *
 * Paged modes (Heil síða, Tvær síður) share one code path — the spread machinery, with the pages per
 * spread read from the current layout on every call: spreads fitted to the viewport, no scrolling
 * between them; turned instantly by the left/right third (view mode, zoom 1; never in edit mode), a
 * swipe (zoom 1; one finger where fingers navigate, two fingers in edit mode where one finger draws)
 * or the page-turn keys. A tap or swipe that cannot turn shows "Fyrsta síða" / "Síðasta síða" (on
 * the last page only when the song page offers nothing next — see SheetViewerEnv.onNextPastEnd).
 * Every page stays mounted — pages of other spreads are hidden — so the decode scheduler
 * pre-decodes the next/previous spread and connector targets, and jumps only flip visibility (the
 * target page becomes the current one). Pinch zoom zooms the spread itself (content = viewport ×
 * zoom, panned natively); while zoomed, taps never turn (a double-tap anywhere returns to zoom 1) and
 * keys turn to the next spread's first page at its top-left, keeping the zoom. Switching layouts
 * keeps the current page and returns to zoom 1. The page reported as current (onPageChange,
 * env.onPage, the position, mode switches) is the page the user went to (goToPage / jumpTo) while
 * its spread shows, else the spread's first. A one-time hint explains the tap zones and swipes the
 * first time each paged mode shows in view mode.
 *
 * Known gap (offline owner / lead): two-up asks for the 'm' tier, but offline/sources serves a
 * stored 'l' bundle instead (iPad packs store 'l'); at ≈ 10.7 MP per 'l' page the shown spread
 * fills most of the 24 MP decode budget, so the next/previous spread and connector targets are
 * rarely pre-decoded from a pack on 11"/Air/mini/10th-gen iPads (they show 's' first, then sharpen).
 *
 * Performance rules (iPad): native overflow scroller with touch-action pan-x pan-y and passive
 * listeners only; React state changes only when the visible page set changes; the 's' tier paints
 * immediately and is never removed; large tiers go through the decode scheduler; jumps are
 * synchronous scroll assignments (never awaiting anything).
 */
import {
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type Ref,
} from 'react';
import type { SheetTier, SongFileDTO } from '../../shared/dto/library';
import { planDecodes } from './decodePlan';
import { useDecodeScheduler } from './decodeScheduler';
import { SheetViewerEnvContext, type TurnVia } from './env';
import { BACK_PILL_MS, usePhonePortrait } from './backPill';
import { JumpStack } from './jumpStack';
import {
  clampZoom,
  computeLayout,
  pageAtY,
  pageScrollTop,
  positionFromScroll,
  scrollForPoint,
  scrollFromPosition,
  visiblePages,
  type Layout,
  type PagesPerSpread,
  type VisibleInfo,
} from './layout';
import { effectiveMode, toggleWholePage, usePageModePrefs } from './pageMode';
import { PageView, type PulseState } from './PageView';
import { bestLarge, useSheetImages } from './sheetImages';
import {
  computeSpreadLayout,
  perSpreadOf,
  pointInPages,
  spreadCount,
  spreadFromPosition,
  spreadNeighbours,
  spreadOf,
  spreadPages,
  spreadPosition,
  spreadScrollForPage,
  spreadScrollForPoint,
  spreadVisible,
  swipeDirection,
  tapZone,
  twoUpEligible,
} from './spreads';
import { PREF, readPref, writePref } from './storage';
import { chooseTier, decodeBudgetMp, requiredPixelWidth } from './tiers';
import {
  MAX_ZOOM,
  MIN_ZOOM,
  type ConnectorPin,
  type JumpOptions,
  type PagePoint,
  type RenderPageOverlay,
  type ViewerHandle,
  type ViewerMode,
  type ViewerPosition,
} from './types';
import { usePageKeys } from './usePageKeys';
import { usePinchZoom } from './usePinchZoom';
import { BackPill, EdgeHint, FitHint, HqErrorChip, loadErrorKind, LoadErrorOverlay, LoadingPill, PageIndicator, PagedHint } from './ViewerChrome';

/** Edit mode: 'none' when a finger draws, 'pan-x pan-y' when fingers scroll natively (pen draws). */
export type EditTouchAction = 'none' | 'pan-x pan-y';

export interface SheetViewerProps {
  /** A sheet file with `pages` and at least the 's' tier. Remount (key) when id/rev changes. */
  file: SongFileDTO;
  mode?: ViewerMode;
  connectors?: readonly ConnectorPin[];
  renderPageOverlay?: RenderPageOverlay;
  /** false while hidden behind another tab: disables page-turn keys. */
  active?: boolean;
  /** Called once when the media route says this revision is gone (404) — refetch the song. */
  onStaleRevision?: () => void;
  onPageChange?: (page: number, count: number) => void;
  /** Edit mode: pins stay tappable and call onPinTap (Tengill / Velja tools) instead of being inert. */
  pinsInteractive?: boolean;
  onPinTap?: (pin: ConnectorPin) => void;
  /** Edit mode: touch-action of the page boxes (default 'none'). */
  editTouchAction?: EditTouchAction;
  ref?: Ref<ViewerHandle>;
}

type Pending =
  | { kind: 'scroll'; top: number; left: number }
  | { kind: 'page'; page: number }
  | { kind: 'anchor'; pos: ViewerPosition }
  | { kind: 'point'; page: number; x: number; y: number };

const EMPTY_VIS: VisibleInfo = { first: 0, last: -1, current: 0, ordered: [] };
const MOMENTUM_MS = 150;
const DOUBLE_TAP_MS = 350;
/** Touches are not swipes for this long after an Apple Pencil touch (palm). */
const STYLUS_GRACE_MS = 600;
/** A two-finger swipe must not change the finger distance by more than this (else it is a pinch). */
const TWO_FINGER_MAX_SCALE = 0.04;
/** Touches starting here never turn spreads (pins, buttons, annotation handles, text boxes). */
const NO_SWIPE_SELECTOR = 'button, a, input, textarea, select, [data-annot-ignore], [data-annot-handle], [data-no-swipe]';

const isBool = (v: unknown): v is boolean => typeof v === 'boolean';
/** How long the one-time paged-mode hint stays. */
const PAGED_HINT_MS = 5000;
/** How long "Fyrsta síða" / "Síðasta síða" stays. */
const EDGE_HINT_MS = 1200;

/** Layout identity for mode switches: the scroll layout, or the spread layout with its pages per spread. */
const kindKey = (L: Layout): string => (L.kind === 'spread' ? `spread:${perSpreadOf(L)}` : 'scroll');

function sameVis(a: VisibleInfo, b: VisibleInfo): boolean {
  return (
    a.first === b.first &&
    a.last === b.last &&
    a.current === b.current &&
    a.ordered.length === b.ordered.length &&
    a.ordered.every((v, i) => v === b.ordered[i])
  );
}

const isStylus = (t: Touch): boolean => (t as Touch & { touchType?: string }).touchType === 'stylus';
const anyStylus = (list: TouchList): boolean => {
  for (let i = 0; i < list.length; i++) if (isStylus(list[i]!)) return true;
  return false;
};

export function SheetViewer({
  file,
  mode = 'view',
  connectors,
  renderPageOverlay,
  active = true,
  onStaleRevision,
  onPageChange,
  pinsInteractive = false,
  onPinTap,
  editTouchAction = 'none',
  ref,
}: SheetViewerProps) {
  const pages = useMemo(() => file.pages ?? [], [file.pages]);
  const count = pages.length;
  const env = useContext(SheetViewerEnvContext);
  const envRef = useRef(env);
  envRef.current = env;

  const scrollerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [vp, setVp] = useState<{ w: number; h: number } | null>(null);
  const [zoom, setZoomState] = useState(1);
  const zoomRef = useRef(1);
  const prefs = usePageModePrefs();
  const eligible = !!vp && twoUpEligible(vp.w, vp.h);
  const layoutMode = effectiveMode(prefs, eligible);
  const paged = layoutMode !== 'scroll' && count > 0;
  const perSpread: PagesPerSpread = layoutMode === 'twoUp' ? 2 : 1;
  const layout = useMemo<Layout | null>(() => {
    if (!vp || count === 0) return null;
    return paged
      ? computeSpreadLayout({ pages, viewportW: vp.w, viewportH: vp.h, zoom, perSpread })
      : computeLayout({ pages, viewportW: vp.w, viewportH: vp.h, fit: 'width', zoom });
  }, [pages, count, vp, zoom, paged, perSpread]);
  const layoutRef = useRef<Layout | null>(null);
  /** kindKey of the last layout (a change = a mode switch). */
  const kindRef = useRef<string | null>(null);
  const [vis, setVis] = useState<VisibleInfo>(EMPTY_VIS);
  /** Paged modes: the spread showing (state for rendering, ref for synchronous reads). */
  const [spread, setSpread] = useState(0);
  const spreadRef = useRef(0);
  /** Paged modes: the page the user went to (goToPage / jumpTo / restore); null after a plain turn. */
  const focusRef = useRef<number | null>(null);
  /** Page shown as "current" (largest visible / the spread's page in focus): kept across mode switches. */
  const currentPageRef = useRef(0);
  if (vis.last >= 0) currentPageRef.current = vis.current;
  const anchorRef = useRef<ViewerPosition | null>(null);
  const pendingRef = useRef<Pending | null>(null);
  const lastScrollAtRef = useRef(0);
  const stackRef = useRef(new JumpStack());
  const pulseSeq = useRef(0);
  const [pulse, setPulse] = useState<PulseState | null>(null);
  const [backKey, setBackKey] = useState<number | null>(null);
  // A phone held upright gets a smaller, shorter-lived back pill (backPill.ts).
  const phonePortrait = usePhonePortrait();
  const [fitHint, setFitHint] = useState<{ key: number; mode: 'scroll' | 'page' } | null>(null);
  const [pagedHint, setPagedHint] = useState(false);
  const [edgeHint, setEdgeHint] = useState<{ key: number; edge: 'first' | 'last' } | null>(null);
  const edgeSeq = useRef(0);
  const lastTapRef = useRef<{ t: number; x: number; y: number } | null>(null);

  /* ── scroll bookkeeping ──────────────────────────────────────────────── */

  const syncFromScroll = useCallback(() => {
    const el = scrollerRef.current;
    const L = layoutRef.current;
    if (!el || !L) return;
    let v: VisibleInfo;
    if (L.kind === 'spread') {
      anchorRef.current = spreadPosition(L, spreadRef.current, el.scrollTop, el.scrollLeft, focusRef.current);
      v = spreadVisible(spreadRef.current, L.boxes.length, focusRef.current, perSpreadOf(L));
    } else {
      anchorRef.current = positionFromScroll(L, el.scrollTop, el.scrollLeft);
      v = visiblePages(L, el.scrollTop, el.clientHeight);
    }
    setVis((prev) => (sameVis(prev, v) ? prev : v));
  }, []);

  /** Synchronous scroll; stops iOS momentum first so the assignment sticks. */
  const instantScroll = useCallback(
    (top: number, left: number) => {
      const el = scrollerRef.current;
      if (!el) return;
      const momentum = performance.now() - lastScrollAtRef.current < MOMENTUM_MS;
      if (momentum) el.style.overflow = 'hidden';
      el.scrollTop = top;
      el.scrollLeft = left;
      if (momentum) {
        requestAnimationFrame(() => {
          el.style.overflow = '';
        });
      }
      syncFromScroll();
    },
    [syncFromScroll],
  );

  /**
   * Paged modes: shows spread `index` (clamped) at the given scroll offsets — synchronous.
   * `focus` = the page the user went to (reported as current while it belongs to the spread).
   */
  const showSpread = useCallback(
    (index: number, top: number, left: number, focus: number | null = null) => {
      const L = layoutRef.current;
      const n = L ? spreadCount(L.boxes.length, perSpreadOf(L)) : 0;
      const s = n > 0 ? Math.min(n - 1, Math.max(0, index)) : 0;
      focusRef.current = focus;
      if (s !== spreadRef.current) {
        spreadRef.current = s;
        setSpread(s);
        setPagedHint(false);
        setEdgeHint(null);
      }
      instantScroll(top, left);
    },
    [instantScroll],
  );

  /** Restores a position in the current layout (spread: the spread containing its page). */
  const scrollToPosition = useCallback(
    (pos: ViewerPosition) => {
      const L = layoutRef.current;
      if (!L) return;
      if (L.kind === 'spread') {
        const r = spreadFromPosition(L, pos);
        showSpread(r.spread, r.top, r.left, pos.page);
      } else {
        const s = scrollFromPosition(L, pos);
        instantScroll(s.top, s.left);
      }
    },
    [instantScroll, showSpread],
  );

  const applyPending = useCallback(
    (p: Pending, L: Layout) => {
      if (L.kind === 'spread') {
        const n = L.boxes.length;
        const per = perSpreadOf(L);
        switch (p.kind) {
          case 'scroll':
            instantScroll(p.top, p.left);
            break;
          case 'page': {
            const s = spreadScrollForPage(L, p.page);
            showSpread(spreadOf(p.page, n, per), s.top, s.left, p.page);
            break;
          }
          case 'anchor':
            scrollToPosition(p.pos);
            break;
          case 'point': {
            const s = spreadScrollForPoint(L, p.page, p.x, p.y);
            showSpread(spreadOf(p.page, n, per), s.top, s.left, p.page);
            break;
          }
        }
        return;
      }
      switch (p.kind) {
        case 'scroll':
          instantScroll(p.top, p.left);
          break;
        case 'page':
          instantScroll(pageScrollTop(L, p.page), 0);
          break;
        case 'anchor':
          scrollToPosition(p.pos);
          break;
        case 'point': {
          const s = scrollForPoint(L, p.page, p.x, p.y);
          instantScroll(s.top, s.left);
          break;
        }
      }
    },
    [instantScroll, showSpread, scrollToPosition],
  );

  // Measure the viewport; ignore 0-sized reports (hidden tab) so the layout survives.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w < 2 || h < 2) return;
      setVp((prev) => (prev && prev.w === w && prev.h === h ? prev : { w, h }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pinch = usePinchZoom({
    scrollerRef,
    contentRef,
    getZoom: () => zoomRef.current,
    minZoom: MIN_ZOOM,
    maxZoom: MAX_ZOOM,
    // Non-WebKit two-finger pinch (WebKit uses gesture events). In edit mode M4 pans with two
    // fingers itself (annotate/useEditInput) while this keeps the live pinch feedback.
    touchFallback: true,
    onCommit: (k, ox, oy) => commitZoom(k, ox, oy),
  });
  const pinchRef = useRef(pinch);
  pinchRef.current = pinch;

  // New layout (resize, zoom, page layout): keep the reading position anchored. Switching between
  // Samfellt, Heil síða and Tvær síður keeps the current page (the spread containing it / its top)
  // and returns to zoom 1 (a zoomed spread would not take swipes or tap-zone turns).
  useLayoutEffect(() => {
    layoutRef.current = layout;
    const prevKind = kindRef.current;
    if (layout) kindRef.current = kindKey(layout);
    const el = scrollerRef.current;
    if (!el || !layout) return;
    const content = contentRef.current;
    if (content && !pinch.isPinching()) {
      content.style.transform = '';
      content.style.transformOrigin = '';
      content.style.removeProperty('--kgr-pin-scale');
    }
    const switched = !!prevKind && prevKind !== kindKey(layout);
    if (switched && zoomRef.current !== 1) {
      // Re-laid out at zoom 1 right away (state set in a layout effect renders before paint).
      pendingRef.current = { kind: 'page', page: currentPageRef.current };
      zoomRef.current = 1;
      setZoomState(1);
      return;
    }
    let p = pendingRef.current;
    pendingRef.current = null;
    if (!p && switched) p = { kind: 'page', page: currentPageRef.current };
    if (p) applyPending(p, layout);
    else if (anchorRef.current) scrollToPosition(anchorRef.current);
    else syncFromScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      lastScrollAtRef.current = performance.now();
      if (!raf) {
        raf = requestAnimationFrame(() => {
          raf = 0;
          syncFromScroll();
        });
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [syncFromScroll]);

  /* ── zoom ────────────────────────────────────────────────────────────── */

  const clearTransform = () => {
    const content = contentRef.current;
    if (!content) return;
    content.style.transform = '';
    content.style.transformOrigin = '';
    content.style.removeProperty('--kgr-pin-scale');
  };

  function commitZoom(k: number, ox: number, oy: number) {
    const el = scrollerRef.current;
    const z0 = zoomRef.current;
    let z1 = clampZoom(z0 * k, MIN_ZOOM, MAX_ZOOM);
    if (Math.abs(z1 - 1) < 0.05) z1 = 1;
    z1 = Math.round(z1 * 100) / 100;
    const kk = z1 / z0;
    if (!el || Math.abs(kk - 1) < 0.002) {
      clearTransform();
      return;
    }
    pendingRef.current = { kind: 'scroll', top: el.scrollTop + oy * (kk - 1), left: el.scrollLeft + ox * (kk - 1) };
    zoomRef.current = z1;
    setZoomState(z1);
  }

  const zoomAround = useCallback((z: number, focal?: { clientX: number; clientY: number }) => {
    const el = scrollerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const fx = focal ? focal.clientX - rect.left : el.clientWidth / 2;
    const fy = focal ? focal.clientY - rect.top : el.clientHeight / 2;
    commitZoom(z / zoomRef.current, el.scrollLeft + fx, el.scrollTop + fy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── navigation ──────────────────────────────────────────────────────── */

  const currentPosition = useCallback((): ViewerPosition | null => {
    const el = scrollerRef.current;
    const L = layoutRef.current;
    if (!el || !L) return null;
    if (L.kind === 'spread') return spreadPosition(L, spreadRef.current, el.scrollTop, el.scrollLeft, focusRef.current);
    return positionFromScroll(L, el.scrollTop, el.scrollLeft);
  }, []);

  const jumpTo = useCallback(
    (page: number, x: number, y: number, opts?: JumpOptions) => {
      const L = layoutRef.current;
      if (!L) {
        pendingRef.current = { kind: 'point', page, x, y };
        return;
      }
      if (opts?.remember ?? true) {
        const pos = currentPosition();
        if (pos) {
          stackRef.current.push({ position: pos, source: opts?.source, at: Date.now() });
          setBackKey(Date.now());
        }
      }
      // A jump always lands at normal zoom, never on a magnified spot; the layout effect applies it.
      if (zoomRef.current !== 1) {
        pendingRef.current = { kind: 'point', page, x, y };
        zoomRef.current = 1;
        setZoomState(1);
      } else {
        applyPending({ kind: 'point', page, x, y }, L);
      }
      if (opts?.pulse ?? true) setPulse({ key: ++pulseSeq.current, page, x, y });
    },
    [currentPosition, applyPending],
  );

  const jumpBack = useCallback(() => {
    const entry = stackRef.current.pop();
    setBackKey(null);
    if (!entry || !layoutRef.current) return;
    // Back to where the jump started, including the zoom the user had.
    if (Number.isFinite(entry.position.zoom) && entry.position.zoom !== zoomRef.current) {
      pendingRef.current = { kind: 'anchor', pos: entry.position };
      zoomRef.current = entry.position.zoom;
      setZoomState(entry.position.zoom);
    } else {
      scrollToPosition(entry.position);
    }
    if (entry.source) setPulse({ key: ++pulseSeq.current, ...entry.source });
  }, [scrollToPosition]);

  const goToPage = useCallback(
    (page: number) => {
      const L = layoutRef.current;
      if (!L) {
        pendingRef.current = { kind: 'page', page };
        return;
      }
      if (L.kind === 'spread') applyPending({ kind: 'page', page }, L);
      else instantScroll(pageScrollTop(L, page), scrollerRef.current?.scrollLeft ?? 0);
    },
    [instantScroll, applyPending],
  );

  /** "Fyrsta síða" / "Síðasta síða": a tap or swipe that could not turn (keys and pedals never show it). */
  const showEdge = useCallback((edge: 'first' | 'last') => {
    setPagedHint(false);
    setEdgeHint({ key: ++edgeSeq.current, edge });
  }, []);

  /** One page (scroll) / spread (paged) forward or back; `repeat` = an auto-repeated keydown. */
  const turnPage = useCallback(
    (dir: 1 | -1, via: TurnVia, repeat = false) => {
      const el = scrollerRef.current;
      const L = layoutRef.current;
      if (!el || !L || L.boxes.length === 0) return;
      if (L.kind === 'spread') {
        const n = L.boxes.length;
        const per = perSpreadOf(L);
        const s = spreadRef.current;
        if (dir > 0 && s >= spreadCount(n, per) - 1) {
          // Past the end: the song page decides (setlist: the "Næsta lag ›" chip; never a song skip
          // from a tap). Only when nothing follows does a tap / swipe say "Síðasta síða".
          const onNext = envRef.current.onNextPastEnd;
          const result = onNext ? onNext({ via, repeat }) : 'none';
          if (result === 'none' && via !== 'key') showEdge('last');
        } else if (dir < 0 && s <= 0) {
          if (via !== 'key') showEdge('first');
        } else {
          // Zoomed in: start at the new spread's first page, top-left (0, 0 at zoom 1).
          const first = spreadPages(s + dir, n, per)[0] ?? 0;
          const o = spreadScrollForPage(L, first);
          showSpread(s + dir, o.top, o.left);
        }
        return;
      }
      const top = el.scrollTop;
      // Already at the very end: "next" asks for what follows (setlist: the next song).
      if (dir > 0 && top + el.clientHeight >= el.scrollHeight - 2) {
        envRef.current.onNextPastEnd?.({ via, repeat });
        return;
      }
      const p = pageAtY(L, top + L.padTop + 1);
      let target: number;
      if (dir > 0) target = p >= L.boxes.length - 1 ? L.height : pageScrollTop(L, p + 1);
      else {
        const curTop = pageScrollTop(L, p);
        target = top > curTop + 4 ? curTop : pageScrollTop(L, Math.max(0, p - 1));
      }
      instantScroll(target, el.scrollLeft);
    },
    [instantScroll, showSpread, showEdge],
  );

  /** Double-tap: back to zoom 1 when zoomed in, else Samfellt ↔ Heil síða (two-up: nothing). */
  const toggleFit = useCallback(() => {
    const el = scrollerRef.current;
    const L = layoutRef.current;
    if (!el || !L) return;
    if (zoomRef.current !== 1) {
      const pos = currentPosition();
      if (pos) pendingRef.current = { kind: 'anchor', pos: { ...pos, left: 0, ...(L.kind === 'spread' ? { top: 0 } : {}) } };
      zoomRef.current = 1;
      setZoomState(1);
      return;
    }
    if (L.kind === 'spread' && perSpreadOf(L) === 2) return; // two-up always fits the whole spread
    const page = L.kind === 'spread' ? currentPageRef.current : visiblePages(L, el.scrollTop, el.clientHeight).current;
    pendingRef.current = { kind: 'page', page };
    // The first switch to Heil síða shows the paged hint instead (they would overlap).
    const firstPaged = L.kind === 'scroll' && !readPref(PREF.pageHint, false, isBool);
    const next = toggleWholePage();
    if (!firstPaged) setFitHint({ key: Date.now(), mode: next === 'page' ? 'page' : 'scroll' });
  }, [currentPosition]);

  const pointFromClient = useCallback((clientX: number, clientY: number): PagePoint | null => {
    const el = scrollerRef.current;
    const L = layoutRef.current;
    if (!el || !L) return null;
    const rect = el.getBoundingClientRect();
    const cx = el.scrollLeft + clientX - rect.left;
    const cy = el.scrollTop + clientY - rect.top;
    if (L.kind === 'spread') return pointInPages(L, spreadVisible(spreadRef.current, L.boxes.length, null, perSpreadOf(L)).ordered, cx, cy);
    const page = pageAtY(L, cy);
    return pointInPages(L, [page], cx, cy);
  }, []);

  useImperativeHandle(
    ref,
    (): ViewerHandle => ({
      jumpTo,
      getPosition: currentPosition,
      restorePosition: (pos) => {
        const L = layoutRef.current;
        const z = Number.isFinite(pos.zoom) ? Math.round(clampZoom(pos.zoom, MIN_ZOOM, MAX_ZOOM) * 100) / 100 : zoomRef.current;
        if (!L || z !== zoomRef.current) {
          // Applied by the next layout (first layout, or the one for the restored zoom).
          pendingRef.current = { kind: 'anchor', pos };
          if (z !== zoomRef.current) {
            zoomRef.current = z;
            setZoomState(z);
          }
          return;
        }
        scrollToPosition(pos);
      },
      goToPage,
      getScrollElement: () => scrollerRef.current,
      getZoom: () => zoomRef.current,
      setZoom: zoomAround,
      pointFromClient,
      cancelSwipe: () => cancelSwipeRef.current(),
      turnPage: (dir) => turnPage(dir, 'button'),
    }),
    [jumpTo, currentPosition, scrollToPosition, goToPage, zoomAround, pointFromClient, turnPage],
  );

  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    if (mode !== 'view') return;
    if ((e.target as HTMLElement).closest('button, a, input, textarea, select, [data-no-dbltap]')) return;
    const el = scrollerRef.current;
    if (layoutRef.current?.kind === 'spread' && el && zoomRef.current === 1) {
      // Paged at zoom 1: the left / right third turns the page / spread at once (no double-tap
      // wait); the middle third keeps the double-tap. Zoomed in, every tap counts towards the
      // double-tap that returns to zoom 1.
      const rect = el.getBoundingClientRect();
      const zone = tapZone(e.clientX - rect.left, rect.width);
      if (zone !== 0) {
        lastTapRef.current = null;
        turnPage(zone, 'tap');
        return;
      }
    }
    const now = performance.now();
    const last = lastTapRef.current;
    if (last && now - last.t < DOUBLE_TAP_MS && Math.hypot(e.clientX - last.x, e.clientY - last.y) < 40) {
      lastTapRef.current = null;
      toggleFit();
    } else lastTapRef.current = { t: now, x: e.clientX, y: e.clientY };
  };

  // Page-turn keys and Bluetooth pedals.
  const turnByKey = useCallback((dir: 1 | -1, repeat: boolean) => turnPage(dir, 'key', repeat), [turnPage]);
  usePageKeys(active, turnByKey);

  // Paged-mode swipes (passive listeners; native scrolling is untouched). One finger where fingers
  // navigate (view mode, or edit mode with the Pencil drawing); two fingers in edit mode where one
  // finger draws. Only at zoom 1 (zoomed in, fingers pan the spread); never with the Pencil or a
  // palm right after it.
  const swipeRef = useRef({ one: true, two: false });
  swipeRef.current = { one: mode === 'view' || editTouchAction === 'pan-x pan-y', two: mode === 'edit' && editTouchAction === 'none' };
  /** Drops the running swipe track (ViewerHandle.cancelSwipe: a finger that grabbed a mark in edit mode). */
  const cancelSwipeRef = useRef<() => void>(() => undefined);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !paged) return;
    let track: { n: number; x0: number; y0: number; d0: number; x: number; y: number; d: number; t0: number } | null = null;
    let stylusUntil = 0;
    cancelSwipeRef.current = () => {
      track = null;
    };
    const geom = (list: TouchList) => {
      const a = list[0]!;
      const b = list[1];
      if (!b) return { x: a.clientX, y: a.clientY, d: 0 };
      return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2, d: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1 };
    };
    const onStart = (e: TouchEvent) => {
      const now = performance.now();
      if (anyStylus(e.touches)) {
        track = null;
        stylusUntil = now + STYLUS_GRACE_MS;
        return;
      }
      const target = e.target instanceof Element ? e.target : null;
      if (now < stylusUntil || e.touches.length > 2 || !target || target.closest(NO_SWIPE_SELECTOR)) {
        track = null;
        return;
      }
      const g = geom(e.touches);
      track = { n: e.touches.length, x0: g.x, y0: g.y, d0: g.d, x: g.x, y: g.y, d: g.d, t0: now };
    };
    const onMove = (e: TouchEvent) => {
      if (!track) return;
      if (anyStylus(e.touches)) {
        track = null;
        return;
      }
      if (e.touches.length !== track.n) return;
      const g = geom(e.touches);
      track.x = g.x;
      track.y = g.y;
      track.d = g.d;
    };
    const onEnd = (e: TouchEvent) => {
      const now = performance.now();
      if (anyStylus(e.changedTouches)) {
        track = null;
        stylusUntil = now + STYLUS_GRACE_MS;
        return;
      }
      const tr = track;
      track = null;
      if (!tr) return;
      let dir: -1 | 0 | 1 = 0;
      if (tr.n === 1) {
        const t = e.changedTouches[0];
        if (!t || e.touches.length > 0 || !swipeRef.current.one) return;
        dir = swipeDirection(t.clientX - tr.x0, t.clientY - tr.y0, now - tr.t0);
      } else {
        // Decided when the first of the two fingers lifts, from the last two-finger position.
        if (!swipeRef.current.two || Math.abs(tr.d / tr.d0 - 1) > TWO_FINGER_MAX_SCALE) return;
        dir = swipeDirection(tr.x - tr.x0, tr.y - tr.y0, now - tr.t0);
      }
      if (dir !== 0 && zoomRef.current === 1 && !pinchRef.current.isPinching()) turnPage(dir, 'swipe');
    };
    const onCancel = () => {
      track = null;
    };
    const passive: AddEventListenerOptions = { passive: true };
    el.addEventListener('touchstart', onStart, passive);
    el.addEventListener('touchmove', onMove, passive);
    el.addEventListener('touchend', onEnd, passive);
    el.addEventListener('touchcancel', onCancel, passive);
    return () => {
      cancelSwipeRef.current = () => undefined;
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onCancel);
    };
  }, [paged, turnPage]);

  // Back pill lives BACK_PILL_MS (4 s) after each jump.
  useEffect(() => {
    if (backKey === null) return;
    const timer = setTimeout(() => setBackKey(null), BACK_PILL_MS);
    return () => clearTimeout(timer);
  }, [backKey]);

  useEffect(() => {
    if (!fitHint) return;
    const timer = setTimeout(() => setFitHint(null), 1400);
    return () => clearTimeout(timer);
  }, [fitHint]);

  useEffect(() => {
    if (!edgeHint) return;
    const timer = setTimeout(() => setEdgeHint(null), EDGE_HINT_MS);
    return () => clearTimeout(timer);
  }, [edgeHint]);

  // One-time hint the first time each paged mode (Heil síða, Tvær síður) shows in view mode (tap
  // zones and swipes have no label); gone after a few seconds or at the first turn.
  const spreadShown = layout?.kind === 'spread' && mode === 'view' && active;
  const hintPref = layout?.kind === 'spread' && perSpreadOf(layout) === 1 ? PREF.pageHint : PREF.twoUpHint;
  useEffect(() => {
    if (!spreadShown || readPref(hintPref, false, isBool)) return;
    writePref(hintPref, true);
    setPagedHint(true);
  }, [spreadShown, hintPref]);
  useEffect(() => {
    if (!pagedHint) return;
    if (!spreadShown) {
      setPagedHint(false);
      return;
    }
    const timer = setTimeout(() => setPagedHint(false), PAGED_HINT_MS);
    return () => clearTimeout(timer);
  }, [pagedHint, spreadShown]);

  useEffect(() => {
    if (vis.last >= 0) onPageChange?.(vis.current, count);
  }, [vis.current, vis.last, count, onPageChange]);
  // Song page (song tabs): the page of this file revision, to reopen it there.
  useEffect(() => {
    if (vis.last >= 0) envRef.current.onPage?.(file.id, file.rev, vis.current);
  }, [vis.current, vis.last, file.id, file.rev]);

  // Song page: whether two-up is possible at this size, and whether the last page is in view.
  const measured = vp !== null;
  useEffect(() => {
    if (!measured) return;
    envRef.current.onTwoUpEligible?.(eligible);
  }, [eligible, measured]);
  const atEnd = count > 0 && vis.last === count - 1;
  useEffect(() => {
    envRef.current.onAtEnd?.(atEnd);
  }, [atEnd]);

  /* ── images & decode scheduling ─────────────────────────────────────── */

  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const availableTiers = useMemo(() => (Object.keys(file.tiers ?? {}) as SheetTier[]).filter((k) => !!file.tiers?.[k]), [file.tiers]);
  const baseWidth = layout ? Math.max(...layout.boxes.map((b) => b.width)) / layout.zoom : 0;
  const wantedTier = layout ? chooseTier(availableTiers, requiredPixelWidth(baseWidth, dpr, zoom)) : null;
  const { images, retry } = useSheetImages(file, wantedTier);
  const best = useMemo(() => bestLarge(images, count), [images, count]);
  const scheduler = useDecodeScheduler();
  const budget = useMemo(
    () => decodeBudgetMp(typeof screen !== 'undefined' ? screen.width : 0, typeof screen !== 'undefined' ? screen.height : 0, dpr),
    [dpr],
  );

  const pinsByPage = useMemo(() => {
    const m = new Map<number, ConnectorPin[]>();
    for (const c of connectors ?? []) {
      if (c.page < 0 || c.page >= count) continue;
      const list = m.get(c.page) ?? [];
      list.push(c);
      m.set(c.page, list);
    }
    return m;
  }, [connectors, count]);

  const targets = useMemo(() => {
    const out: number[] = [];
    for (const p of vis.ordered) for (const c of pinsByPage.get(p) ?? []) if (c.target.page >= 0 && c.target.page < count) out.push(c.target.page);
    return out;
  }, [vis.ordered, pinsByPage, count]);

  // Paged: the neighbours are the next and previous spread — ±1 page in Heil síða, ±2 in two-up
  // (scroll mode: ±1 page, the planner's default).
  const layoutPer = layout?.kind === 'spread' ? perSpreadOf(layout) : null;
  const spreadNbrs = useMemo(
    () => (layoutPer !== null && vis.last >= 0 ? spreadNeighbours(spreadOf(vis.first, count, layoutPer), count, layoutPer) : undefined),
    [layoutPer, vis.first, vis.last, count],
  );

  useEffect(() => {
    const desired = planDecodes({
      pageCount: count,
      visible: vis.ordered,
      targets,
      neighbours: spreadNbrs,
      available: (i) => !!best[i]?.url,
      megapixels: (i) => best[i]?.mp ?? 0,
      budgetMp: budget,
    });
    scheduler.setPlan(desired, (i) => best[i]?.url ?? null);
  }, [vis.ordered, targets, spreadNbrs, best, budget, count, scheduler]);

  const overlayPages = useMemo(() => {
    const s = new Set<number>();
    if (!renderPageOverlay || vis.last < 0) return s;
    if (spreadNbrs) for (const p of [...vis.ordered, ...spreadNbrs]) s.add(p);
    else for (let i = Math.max(0, vis.first - 2); i <= Math.min(count - 1, vis.last + 2); i++) s.add(i);
    for (const tp of targets) s.add(tp);
    return s;
  }, [renderPageOverlay, vis.first, vis.last, vis.ordered, spreadNbrs, targets, count]);

  const onPinRef = useRef<(pin: ConnectorPin) => void>(() => undefined);
  onPinRef.current = (pin) => {
    if (mode !== 'view') {
      if (pinsInteractive) onPinTap?.(pin);
      return;
    }
    jumpTo(pin.target.page, pin.target.x, pin.target.y, { source: { page: pin.page, x: pin.x, y: pin.y } });
  };
  const onPin = useCallback((pin: ConnectorPin) => onPinRef.current(pin), []);
  const onPulseDone = useCallback((key: number) => setPulse((p) => (p?.key === key ? null : p)), []);

  /* ── errors ──────────────────────────────────────────────────────────── */

  const largeError = images.large.some((x) => x.status === 'error');
  const largeLoading = images.large.some((x) => x.status === 'loading');
  // A 404 on any tier means this revision was replaced: ask the parent to refetch the song (which
  // remounts the viewer with the new rev at the same position). If nothing changes within a few
  // seconds, show a normal error + retry.
  const staleNotified = useRef(false);
  const [staleTimedOut, setStaleTimedOut] = useState(false);
  const rawError = images.s.status === 'error' ? loadErrorKind(images.s.error) : null;
  const largeStale = images.large.some((x) => x.status === 'error' && loadErrorKind(x.error) === 'stale');
  const stale = rawError === 'stale' || largeStale;
  useEffect(() => {
    if (!stale || staleNotified.current) return;
    staleNotified.current = true;
    onStaleRevision?.();
    setTimeout(() => setStaleTimedOut(true), 8000);
  }, [stale, onStaleRevision]);
  const awaitingNewRev = stale && !staleTimedOut && !!onStaleRevision;
  const sError = rawError === 'stale' && !awaitingNewRev ? 'failed' : rawError;

  const sLoading = images.s.status !== 'done' && images.s.status !== 'error';
  const spreadMode = layoutPer !== null;
  const shownSpread = layoutPer !== null ? spreadVisible(spread, count, null, layoutPer) : null;

  return (
    <div className="relative h-full w-full overflow-hidden bg-[var(--bg)]">
      <div
        ref={scrollerRef}
        onClick={onClick}
        // Paged at zoom 1 fits the viewport exactly: nothing may scroll (or bounce).
        className={spreadMode && zoom === 1 ? 'absolute inset-0 overflow-hidden' : 'absolute inset-0 overflow-auto'}
        style={{
          touchAction: 'pan-x pan-y',
          overscrollBehavior: 'contain',
          overflowAnchor: 'none',
          scrollbarGutter: 'stable',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div className="relative" style={{ width: layout ? layout.width : '100%', height: layout ? layout.height : '100%' }}>
          <div ref={contentRef} className="absolute inset-0">
            {layout
              ? layout.boxes.map((box, i) => (
                  <PageView
                    key={i}
                    index={i}
                    box={box}
                    sUrl={images.s.urls[i] ?? null}
                    large={scheduler.imagesFor(i)}
                    pins={pinsByPage.get(i)}
                    pulse={pulse?.page === i ? pulse : null}
                    mode={mode}
                    pinsInert={mode === 'edit' && !pinsInteractive}
                    editTouchAction={editTouchAction}
                    hidden={shownSpread ? !shownSpread.ordered.includes(i) : false}
                    showOverlay={overlayPages.has(i)}
                    renderPageOverlay={renderPageOverlay}
                    onPin={onPin}
                    onPulseDone={onPulseDone}
                    registerElement={scheduler.registerElement}
                  />
                ))
              : null}
          </div>
        </div>
      </div>

      {count > 0 ? (
        <PageIndicator
          page={spreadMode ? vis.first + 1 : vis.current + 1}
          last={spreadMode ? vis.last + 1 : undefined}
          count={count}
          busy={sLoading || largeLoading}
        />
      ) : null}
      {fitHint && !pagedHint ? <FitHint key={fitHint.key} mode={fitHint.mode} /> : null}
      {pagedHint && spreadMode ? <PagedHint /> : null}
      {edgeHint ? <EdgeHint key={edgeHint.key} edge={edgeHint.edge} /> : null}
      {backKey !== null ? <BackPill key={backKey} onBack={jumpBack} compact={phonePortrait} /> : null}
      {largeError && !sError && !awaitingNewRev ? <HqErrorChip onRetry={retry} /> : null}
      {sError ? <LoadErrorOverlay kind={sError} onRetry={retry} /> : null}
      {sLoading && images.s.loaded === 0 && count > 0 ? <LoadingPill /> : null}
    </div>
  );
}
